import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle, Clock, ShoppingCart, Gavel, FileText,
  TrendingDown, Activity, Users, Receipt, ChevronRight, Check, X,
  BarChart3, DollarSign, ArrowRight, Bell, Zap, LayoutDashboard, Target, Wallet, UserCog, MessageSquare, Sparkles
} from 'lucide-react';
import { ActionInsights, InformativeInsights, FinanceInsights } from './Dashboard/DashboardModes';
import AIBriefing from './Dashboard/AIBriefing';

const VIEW_MODES = [
  { id: 'action', label: 'Action', icon: Target, desc: 'Tasks, approvals, and live events' },
  { id: 'informative', label: 'Informative', icon: LayoutDashboard, desc: 'Widgets, charts, and analytics' },
  { id: 'finance', label: 'Finance', icon: Wallet, desc: 'Invoices, payments, and spend' },
  { id: 'ai', label: 'Briefing', icon: Sparkles, desc: 'Executive summary with drill-down' },
];

export default function ActionDashboard() {
  const navigate = useNavigate();
  const { user, systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCard, setExpandedCard] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('eyvo_dash_mode') || 'action');
  const [kpiData, setKpiData] = useState(null);

  const userRole = data?.user_role || {};
  const isVendorManager = userRole.is_vendor_manager;

  const switchMode = (mode) => { setViewMode(mode); localStorage.setItem('eyvo_dash_mode', mode); };

  const [savingsData, setSavingsData] = useState(null);
  const [unansweredQs, setUnansweredQs] = useState([]);
  const [insights, setInsights] = useState(null);
  const [briefingPrefetch, setBriefingPrefetch] = useState(null);

  const load = useCallback(async () => {
    try {
      // ── Primary: block only on the main dashboard payload ──
      const r = await api.get('/api/system/action-dashboard');
      setData(r.data);
      setLoading(false);

      // ── Secondary: fire-and-forget, fill in as each resolves ──
      api.get('/api/system/dashboard-stats').then(s => setKpiData(s.data)).catch(() => {});
      api.get('/api/savings/dashboard').then(sv => setSavingsData(sv.data)).catch(() => {});
      api.get('/api/rfx/workflow/unanswered-questions').then(qa => setUnansweredQs(qa.data.questions || [])).catch(() => {});
      api.get('/api/dashboard/insights').then(ins => setInsights(ins.data)).catch(() => {});
    } catch {
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  }, []);

  // Briefing prefetch — runs ONLY after the Action dashboard has rendered, so
  // the user never waits on the briefing-build for their landing screen.
  useEffect(() => {
    if (!data || briefingPrefetch) return;
    const t = setTimeout(() => {
      api.get('/api/dashboard/ai-briefing')
        .then(r => setBriefingPrefetch(r.data))
        .catch(() => {});
    }, 250);  // tiny defer so first paint of Action settles first
    return () => clearTimeout(t);
  }, [data, briefingPrefetch]);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  if (loading) return <AppShell><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" /></div></AppShell>;
  if (!data) return <AppShell><p className="text-center text-slate-400 py-12">Unable to load dashboard.</p></AppShell>;

  const ab = data.action_bar || {};
  const role = data.user_role || {};
  const isFinance = role.is_accounts_manager;
  const isBuyer = role.is_vendor_manager || role.user_type === 'Senior Buyer' || role.user_type === 'Buyer';

  // Build todos list from existing operational data — feeds ActionInsights hero panel
  const todos = (() => {
    const items = [];
    (data.my_approvals || []).forEach(a => {
      const amt = a.amount || 0;
      items.push({
        priority: amt > 10000 ? 'HIGH' : amt > 1000 ? 'MEDIUM' : 'LOW',
        title: `Approve ${a.ref}`,
        subtitle: `${a.type} · ${a.description || ''}`.trim(),
        amount: amt,
      });
    });
    (data.at_risk_items || []).forEach(a => {
      items.push({
        priority: 'HIGH',
        title: `At risk: ${a.ref}`,
        subtitle: a.description || a.status,
        amount: a.amount || 0,
      });
    });
    if ((ab.closing_soon || 0) > 0) items.push({ priority: 'MEDIUM', title: `${ab.closing_soon} RFx event(s) closing soon`, subtitle: 'Sourcing pipeline' });
    if ((ab.live_auctions || 0) > 0) items.push({ priority: 'HIGH', title: `${ab.live_auctions} live auction(s)`, subtitle: 'Monitor bidding' });
    if ((ab.supplier_pending || 0) > 0) items.push({ priority: 'MEDIUM', title: `${ab.supplier_pending} supplier approval(s)`, subtitle: 'Vendor management' });
    return items.sort((a, b) => {
      const order = { HIGH: 0, MEDIUM: 1, LOW: 2 };
      return order[a.priority] - order[b.priority];
    });
  })();

  // If Informative mode, show new graphical insights dashboard
  if (viewMode === 'informative') {
    return (
      <AppShell>
        <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="informative-dashboard">
          <DashboardToggle viewMode={viewMode} switchMode={switchMode} isVendorManager={isVendorManager} />
          <InformativeInsights insights={insights} />
        </div>
      </AppShell>
    );
  }

  // Finance mode
  if (viewMode === 'finance') {
    return (
      <AppShell>
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="finance-dashboard">
          <DashboardToggle viewMode={viewMode} switchMode={switchMode} isVendorManager={isVendorManager} />
          <FinanceInsights insights={insights} />
        </div>
      </AppShell>
    );
  }

  // AI Briefing mode
  if (viewMode === 'ai') {
    return (
      <AppShell>
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto">
          <DashboardToggle viewMode={viewMode} switchMode={switchMode} isVendorManager={isVendorManager} />
          <AIBriefing initialData={briefingPrefetch} />
        </div>
      </AppShell>
    );
  }

  // Action mode (default)
  return (
    <AppShell>
      <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="action-dashboard" role="region" aria-label="Action Dashboard">
        <DashboardToggle viewMode={viewMode} switchMode={switchMode} isVendorManager={isVendorManager} />

        {/* ═══════ GRAPHICAL INSIGHTS HERO ═══════ */}
        {insights && <ActionInsights insights={insights} todos={todos} />}

        {/* ═══════ SECTION 1 — ACTION BAR ═══════ */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <ActionCard icon={CheckCircle} label="Approvals Pending" count={ab.approvals_pending}
            sub={ab.approvals_pending > 0 ? 'Awaiting your action' : 'All clear'}
            color="bg-gradient-to-br from-red-500 to-rose-600"
            expanded={expandedCard === 'approvals'} onToggle={() => setExpandedCard(expandedCard === 'approvals' ? null : 'approvals')} />
          <ActionCard icon={AlertTriangle} label="At Risk / Delayed" count={ab.at_risk}
            sub={ab.at_risk > 0 ? `${ab.at_risk} items delayed > 3 days` : 'Nothing at risk'}
            color="bg-gradient-to-br from-amber-500 to-orange-600"
            expanded={expandedCard === 'risk'} onToggle={() => setExpandedCard(expandedCard === 'risk' ? null : 'risk')} />
          <ActionCard icon={FileText} label="Active Sourcing" count={ab.active_sourcing}
            sub={ab.closing_soon > 0 ? `${ab.closing_soon} closing soon` : 'Events running'}
            color="bg-gradient-to-br from-blue-500 to-indigo-600"
            expanded={expandedCard === 'sourcing'} onToggle={() => setExpandedCard(expandedCard === 'sourcing' ? null : 'sourcing')} />
          <ActionCard icon={Gavel} label="Live Auctions" count={ab.live_auctions}
            sub={ab.live_auctions > 0 ? 'Click to open Control Room' : 'No live auctions'}
            color="bg-gradient-to-br from-violet-500 to-purple-600" pulse={ab.live_auctions > 0}
            expanded={expandedCard === 'auctions'} onToggle={() => setExpandedCard(expandedCard === 'auctions' ? null : 'auctions')} />
          {isFinance ? (
            <ActionCard icon={Receipt} label="Invoices Pending" count={ab.invoices_pending}
              sub="Awaiting processing" color="bg-gradient-to-br from-teal-500 to-emerald-600"
              expanded={expandedCard === 'invoices'} onToggle={() => setExpandedCard(expandedCard === 'invoices' ? null : 'invoices')} />
          ) : (
            <ActionCard icon={Users} label="Tasks Due" count={ab.supplier_pending + (ab.closing_soon || 0)}
              sub={`${ab.supplier_pending > 0 ? ab.supplier_pending + ' vendor approvals' : ''} ${ab.closing_soon > 0 ? ab.closing_soon + ' events closing' : ''}`.trim() || 'No urgent tasks'}
              color="bg-gradient-to-br from-slate-500 to-slate-700"
              expanded={expandedCard === 'tasks'} onToggle={() => setExpandedCard(expandedCard === 'tasks' ? null : 'tasks')} />
          )}
        </div>

        {/* ═══════ EXPANDED DETAIL PANEL ═══════ */}
        {expandedCard && (
          <div className="border rounded-2xl bg-white p-4 animate-in slide-in-from-top-2" data-testid="expanded-detail">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm">
                {expandedCard === 'approvals' && `${ab.approvals_pending} Approvals Awaiting Your Action`}
                {expandedCard === 'risk' && `${ab.at_risk} Items At Risk / Delayed`}
                {expandedCard === 'sourcing' && `${ab.active_sourcing} Active Sourcing Events`}
                {expandedCard === 'auctions' && `${ab.live_auctions} Live Auctions`}
                {expandedCard === 'invoices' && `${ab.invoices_pending} Pending Invoices`}
                {expandedCard === 'tasks' && 'Outstanding Tasks'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setExpandedCard(null)}><X className="w-4 h-4" /></Button>
            </div>

            {/* Approvals detail — click row to navigate */}
            {expandedCard === 'approvals' && (
              <div className="divide-y">
                {(data.my_approvals || []).map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 hover:bg-indigo-50 rounded px-3 -mx-1 cursor-pointer transition-colors"
                    onClick={() => navigate(a.type === 'Order' ? `/orders` : `/requests/${a.ref}`)}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm text-indigo-700">{a.ref}</span>
                      <Badge className="ml-2 text-[9px] bg-slate-100">{a.type}</Badge>
                      <p className="text-xs text-slate-500 truncate">{a.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold">${(a.amount || 0).toLocaleString()}</span>
                      <span className="text-xs text-slate-400">{a.date}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                ))}
                {(data.my_approvals || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No pending approvals</p>}
              </div>
            )}

            {/* At Risk — ONLY the at-risk items */}
            {expandedCard === 'risk' && (
              <div className="divide-y">
                {(data.at_risk_items || []).map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 hover:bg-amber-50 rounded px-3 -mx-1 cursor-pointer transition-colors"
                    onClick={() => navigate(`/requests/${a.ref}`)}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm text-amber-700">{a.ref}</span>
                      <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-700">{a.status}</Badge>
                      <p className="text-xs text-slate-500 truncate">{a.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold">${(a.amount || 0).toLocaleString()}</span>
                      <span className="text-xs text-slate-400">{a.date}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                ))}
                {(data.at_risk_items || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No at-risk items</p>}
              </div>
            )}

            {/* Sourcing detail */}
            {expandedCard === 'sourcing' && (
              <div className="divide-y max-h-[200px] overflow-y-auto">
                {(data.sourcing_events || []).map((e, i) => (
                  <div key={i} className="flex items-center justify-between py-2 hover:bg-slate-50 rounded px-2 -mx-2 cursor-pointer"
                    onClick={() => navigate(`/rfq?open=${e.event_ref}`)}>
                    <div>
                      <span className="text-sm font-semibold">{e.event_ref}</span>
                      <Badge className={`ml-2 text-[9px] ${e.type === 'REVERSE_AUCTION' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`}>
                        {e.type === 'REVERSE_AUCTION' ? 'Auction' : e.type}
                      </Badge>
                      {e.closing_soon && <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700">Closing Soon</Badge>}
                      <p className="text-xs text-slate-500 truncate max-w-sm">{e.title}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right text-xs text-slate-500">
                        <p>{e.closing_date ? new Date(e.closing_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</p>
                        <p>{e.supplier_count} supplier(s)</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Live Auctions detail */}
            {expandedCard === 'auctions' && (
              <div className="divide-y max-h-[200px] overflow-y-auto">
                {(data.sourcing_events || []).filter(e => e.type === 'REVERSE_AUCTION' && e.status === 'OPEN').map((e, i) => (
                  <div key={i} className="flex items-center justify-between py-2 hover:bg-rose-50 rounded px-2 -mx-2 cursor-pointer"
                    onClick={() => navigate(`/rfq?open=${e.event_ref}`)}>
                    <div>
                      <span className="text-sm font-semibold">{e.event_ref}</span>
                      <Badge className="ml-2 text-[9px] bg-rose-100 text-rose-700">Live Auction</Badge>
                      <p className="text-xs text-slate-500 truncate max-w-sm">{e.title}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                ))}
                {(data.sourcing_events || []).filter(e => e.type === 'REVERSE_AUCTION' && e.status === 'OPEN').length === 0 && (
                  <p className="text-sm text-slate-400 py-4 text-center">No live auctions at this time.</p>
                )}
              </div>
            )}

            {/* Invoices detail — show actual pending invoices */}
            {expandedCard === 'invoices' && (
              <div className="divide-y">
                {(data.invoices_pending_list || []).map((inv, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 hover:bg-teal-50 rounded px-3 -mx-1 cursor-pointer transition-colors"
                    onClick={() => navigate('/invoices')}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-sm text-teal-700">{inv.invoice_number || inv.invoice_ref}</span>
                      <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-700 font-semibold">{inv.status}</Badge>
                      <p className="text-xs text-slate-500 truncate">{inv.supplier_name || inv.supplier_code} {inv.order_number ? `· ${inv.order_number}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold">${(inv.amount || inv.total || 0).toLocaleString()}</span>
                      <span className="text-xs text-slate-400">{inv.created_at || ''}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                ))}
                {(data.invoices_pending_list || []).length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No pending invoices</p>}
              </div>
            )}

            {/* Tasks detail */}
            {expandedCard === 'tasks' && (
              <div className="space-y-2">
                {ab.supplier_pending > 0 && <div className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                  <span className="text-sm"><Users className="w-4 h-4 inline mr-2 text-amber-600" />{ab.supplier_pending} supplier registration(s) pending approval</span>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/system-admin')}>Review</Button>
                </div>}
                {ab.closing_soon > 0 && <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                  <span className="text-sm"><Clock className="w-4 h-4 inline mr-2 text-blue-600" />{ab.closing_soon} sourcing event(s) closing within 3 days</span>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/rfq')}>View</Button>
                </div>}
                {ab.live_auctions > 0 && <div className="flex items-center justify-between p-2 bg-violet-50 rounded-lg">
                  <span className="text-sm"><Gavel className="w-4 h-4 inline mr-2 text-violet-600" />{ab.live_auctions} live auction(s) — monitor bidding activity</span>
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => navigate('/rfq')}>Monitor</Button>
                </div>}
              </div>
            )}
          </div>
        )}

        {/* ═══════ SECTION 2 — PRIMARY WORK AREA ═══════ */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* My Approvals (60%) */}
          <div className="lg:col-span-3 border rounded-2xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <h2 className="font-bold text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4 text-red-500" /> My Approvals</h2>
              <Button variant="ghost" size="sm" className="text-xs text-indigo-600" onClick={() => navigate('/approvals')}>View All <ChevronRight className="w-3 h-3 ml-1" /></Button>
            </div>
            {(data.my_approvals || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">No pending approvals — you're all caught up!</div>
            ) : (
              <div className="divide-y max-h-[320px] overflow-y-auto">
                {(data.my_approvals || []).map((a, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-indigo-50 transition-colors cursor-pointer"
                    onClick={() => navigate(a.type === 'Order' ? `/orders` : `/requests/${a.ref}`)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-indigo-700">{a.ref}</span>
                        <Badge className="text-[9px] bg-slate-100 text-slate-500">{a.type}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{a.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold">${(a.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                        <p className="text-[10px] text-slate-400">{a.date}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* My Tasks (40%) */}
          <div className="lg:col-span-2 border rounded-2xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <h2 className="font-bold text-sm flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500" /> My Tasks</h2>
            </div>
            <div className="divide-y max-h-[320px] overflow-y-auto">
              {ab.supplier_pending > 0 && (
                <TaskRow icon={Users} text={`${ab.supplier_pending} supplier(s) pending approval`} type="Vendors" urgent
                  onClick={() => navigate('/system-admin')} />
              )}
              {ab.closing_soon > 0 && (
                <TaskRow icon={FileText} text={`${ab.closing_soon} sourcing event(s) closing soon`} type="Sourcing" urgent
                  onClick={() => navigate('/rfq')} />
              )}
              {ab.live_auctions > 0 && (
                <TaskRow icon={Gavel} text={`${ab.live_auctions} live auction(s) — monitor bids`} type="Auction" urgent
                  onClick={() => navigate('/rfq')} />
              )}
              {ab.invoices_pending > 0 && (
                <TaskRow icon={Receipt} text={`${ab.invoices_pending} invoice(s) awaiting review`} type="Finance"
                  onClick={() => navigate('/invoices')} />
              )}
              {ab.at_risk > 0 && (
                <TaskRow icon={AlertTriangle} text={`${ab.at_risk} requisition(s) delayed > 3 days`} type="Risk"
                  onClick={() => navigate('/requests')} />
              )}
              {ab.approvals_pending === 0 && ab.at_risk === 0 && ab.closing_soon === 0 && ab.live_auctions === 0 && ab.invoices_pending === 0 && ab.supplier_pending === 0 && (
                <div className="px-5 py-8 text-center text-slate-400 text-sm">No outstanding tasks. Great work!</div>
              )}
            </div>
          </div>
        </div>

        {/* ═══════ SECTION 3 — SOURCING + ACTIVITY ═══════ */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Active Sourcing Events */}
          <div className="lg:col-span-3 border rounded-2xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <h2 className="font-bold text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-indigo-500" /> Active Sourcing Events</h2>
              <Button variant="ghost" size="sm" className="text-xs text-indigo-600" onClick={() => navigate('/rfq')}>View All <ChevronRight className="w-3 h-3 ml-1" /></Button>
            </div>
            {(data.sourcing_events || []).length === 0 ? (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">No active sourcing events.</div>
            ) : (
              <div className="divide-y max-h-[260px] overflow-y-auto">
                {(data.sourcing_events || []).map((e, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/rfq?open=${e.event_ref}`)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{e.event_ref}</span>
                        <Badge className={`text-[9px] ${e.type === 'REVERSE_AUCTION' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {e.type === 'REVERSE_AUCTION' ? 'Auction' : e.type}
                        </Badge>
                        {e.closing_soon && <Badge className="text-[9px] bg-amber-100 text-amber-700 animate-pulse">Closing Soon</Badge>}
                        {e.type === 'REVERSE_AUCTION' && e.status === 'OPEN' && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Live</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{e.title}</p>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <p>{e.closing_date ? new Date(e.closing_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</p>
                      <p>{e.supplier_count} supplier(s)</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live Activity Feed */}
          <div className="lg:col-span-2 border rounded-2xl bg-white overflow-hidden">
            <div className="px-5 py-3 border-b bg-slate-50">
              <h2 className="font-bold text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500" /> Live Activity</h2>
            </div>
            <div className="divide-y max-h-[260px] overflow-y-auto">
              {(data.activity || []).length === 0 ? (
                <div className="px-5 py-8 text-center text-slate-400 text-sm">No recent activity.</div>
              ) : (
                (data.activity || []).map((a, i) => (
                  <div key={i} className="px-5 py-2 text-xs">
                    <p className="text-slate-700">{a.text}</p>
                    <p className="text-slate-400 mt-0.5">{a.time ? new Date(a.time).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : ''}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ═══════ SECTION 4 — KPI CARDS ═══════ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard icon={DollarSign} label="Spend This Month" value={`$${(data.kpis?.month_spend || 0).toLocaleString()}`} color="text-emerald-600" onClick={() => navigate('/orders')} />
          <KPICard icon={ShoppingCart} label="Orders This Month" value={String(data.kpis?.month_orders || 0)} color="text-blue-600" onClick={() => navigate('/orders')} />
          <KPICard icon={FileText} label="Active Events" value={String(data.kpis?.active_events || 0)} color="text-indigo-600" onClick={() => navigate('/rfq')} />
          <KPICard icon={TrendingDown} label="Savings YTD" value={savingsData?.totals?.year_to_date?.saving_amount ? `$${savingsData.totals.year_to_date.saving_amount.toLocaleString()}` : '$0'} sub={savingsData?.totals?.year_to_date?.saving_pct ? `${savingsData.totals.year_to_date.saving_pct}% vs baseline` : `${savingsData?.event_count || 0} events calculated`} color="text-amber-600" />
        </div>

        {/* ═══════ SECTION 4b — UNANSWERED RFX QUESTIONS (RFx Group Members) ═══════ */}
        {unansweredQs.length > 0 && (
          <div className="border rounded-2xl bg-white overflow-hidden border-amber-200">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-amber-50">
              <h2 className="font-bold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-amber-500" /> Unanswered Supplier Questions <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-1">{unansweredQs.length}</Badge></h2>
            </div>
            <div className="divide-y max-h-[250px] overflow-y-auto">
              {unansweredQs.map((q, i) => (
                <div key={q.question_id || i} className="px-5 py-3 hover:bg-amber-50/50 cursor-pointer transition-colors" onClick={() => navigate(`/rfq?open=${q.event_ref}`)}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs font-bold text-indigo-700">{q.event_ref}</span>
                    <Badge className="text-[9px]">{q.event_type}</Badge>
                    <span className="text-xs text-slate-400 ml-auto">{q.posted_at ? new Date(q.posted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: tz }) : ''}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800">{q.subject || q.message}</p>
                  <p className="text-xs text-slate-500">From: {q.from_supplier}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══════ SECTION 5 — SUPPLIER MANAGEMENT (Vendor Managers only) ═══════ */}
        {isVendorManager && (
          <div className="border rounded-2xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <h2 className="font-bold text-sm flex items-center gap-2"><UserCog className="w-4 h-4 text-violet-500" /> Supplier Management</h2>
            </div>
            <div className="grid grid-cols-3 divide-x">
              <div className="p-4 text-center cursor-pointer hover:bg-violet-50 transition-colors" onClick={() => setExpandedCard(expandedCard === 'vendors' ? null : 'vendors')}>
                <p className="text-2xl font-black text-violet-700">{ab.supplier_pending || 0}</p>
                <p className="text-xs text-slate-500">Pending Approvals</p>
              </div>
              <div className="p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => navigate('/data/suppliers')}>
                <p className="text-2xl font-black text-slate-700">—</p>
                <p className="text-xs text-slate-500">Active Suppliers</p>
              </div>
              <div className="p-4 text-center cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => navigate('/rfq')}>
                <p className="text-2xl font-black text-blue-700">{ab.active_sourcing || 0}</p>
                <p className="text-xs text-slate-500">Active Sourcing Events</p>
              </div>
            </div>
            {/* Pending Supplier Approvals expanded */}
            {expandedCard === 'vendors' && (data.action_bar?.supplier_pending || 0) > 0 && (
              <div className="border-t p-4">
                <h3 className="font-semibold text-sm mb-2">Suppliers Awaiting Your Approval</h3>
                <div className="divide-y">
                  {(kpiData?.pending_supplier_approvals || []).map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5 hover:bg-violet-50 rounded px-3 -mx-1 cursor-pointer transition-colors"
                      onClick={() => s.approval_token ? window.open(`/approve-supplier/${s.approval_token}`, '_blank') : null}>
                      <div>
                        <span className="font-semibold text-sm text-violet-700">{s.company_name}</span>
                        <p className="text-xs text-slate-500">{s.contact_name} · {s.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-400">{s.registered_at ? new Date(s.registered_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-2">
          <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-300 hover:text-indigo-500 transition-colors">
            Designed by Eyvo, California
          </a>
        </div>
      </div>
    </AppShell>
  );
}


/* ── Sub-components ── */

function ActionCard({ icon: Icon, label, count, sub, color, onToggle, pulse, expanded }) {
  return (
    <button onClick={onToggle} className={`${color} text-white rounded-2xl p-4 text-left transition-colors hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] ${expanded ? 'ring-2 ring-offset-2 ring-white/50' : ''}`}
      data-testid={`action-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 opacity-80" />
        {pulse && <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute h-full w-full rounded-full bg-white opacity-75"/><span className="relative rounded-full h-2.5 w-2.5 bg-white"/></span>}
      </div>
      <p className="text-3xl font-black">{count}</p>
      <p className="text-sm font-semibold opacity-90">{label}</p>
      <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>
    </button>
  );
}

function TaskRow({ icon: Icon, text, type, urgent, onClick }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 text-left transition-colors">
      <Icon className={`w-4 h-4 flex-shrink-0 ${urgent ? 'text-amber-500' : 'text-slate-400'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">{text}</p>
      </div>
      <Badge className={`text-[9px] ${urgent ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{type}</Badge>
      <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
    </button>
  );
}

function KPICard({ icon: Icon, label, value, sub, color, onClick }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag className={`border rounded-2xl bg-white p-4 text-left ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick} aria-label={`${label}: ${value}${sub ? ` — ${sub}` : ''}`}>
      <Icon className={`w-5 h-5 ${color} mb-2`} aria-hidden="true" />
      <p className={`text-2xl font-black ${color}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </Tag>
  );
}



/* ── Dashboard View Toggle ── */

function DashboardToggle({ viewMode, switchMode, isVendorManager }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h1 className="text-xl font-bold text-slate-800">Dashboard</h1>
      <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-1">
        {VIEW_MODES.map(mode => (
          <button key={mode.id} onClick={() => switchMode(mode.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              viewMode === mode.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <mode.icon className="w-3.5 h-3.5" /> {mode.label}
          </button>
        ))}
      </div>
    </div>
  );
}


/* ── Finance Dashboard ── */

function FinanceDashboard({ ab, data, navigate, savingsData, tz }) {
  return (
    <div className="space-y-5" data-testid="finance-dashboard">
      {/* Finance Action Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-red-500 to-rose-600 text-white rounded-2xl p-4 cursor-pointer hover:scale-[1.02] transition-colors" onClick={() => navigate('/invoices')}>
          <Receipt className="w-5 h-5 opacity-80 mb-2" />
          <p className="text-3xl font-black">{ab.invoices_pending || 0}</p>
          <p className="text-sm font-semibold opacity-90">Invoices Pending</p>
          <p className="text-[10px] opacity-70">Awaiting review</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-4">
          <DollarSign className="w-5 h-5 opacity-80 mb-2" />
          <p className="text-3xl font-black">${((data.kpis?.month_spend || 0) / 1000).toFixed(0)}K</p>
          <p className="text-sm font-semibold opacity-90">Spend This Month</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white rounded-2xl p-4">
          <ShoppingCart className="w-5 h-5 opacity-80 mb-2" />
          <p className="text-3xl font-black">{data.kpis?.month_orders || 0}</p>
          <p className="text-sm font-semibold opacity-90">Orders This Month</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl p-4">
          <CheckCircle className="w-5 h-5 opacity-80 mb-2" />
          <p className="text-3xl font-black">{ab.approvals_pending || 0}</p>
          <p className="text-sm font-semibold opacity-90">Pending Approvals</p>
        </div>
      </div>

      {/* Invoice Queue + Payment Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border rounded-2xl bg-white overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h2 className="font-bold text-sm flex items-center gap-2"><Receipt className="w-4 h-4 text-red-500" /> Invoice Queue</h2>
          </div>
          <div className="divide-y max-h-[280px] overflow-y-auto">
            {(data.my_approvals || []).filter(a => a.type === 'Order').length === 0 && (data.my_approvals || []).length === 0 ? (
              <p className="px-5 py-8 text-center text-slate-400 text-sm">No invoices in queue.</p>
            ) : (
              (data.my_approvals || []).map((a, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-2.5 hover:bg-indigo-50 cursor-pointer transition-colors"
                  onClick={() => navigate(a.type === 'Order' ? '/orders' : `/requests/${a.ref}`)}>
                  <div><span className="text-sm font-semibold text-indigo-700">{a.ref}</span><Badge className="ml-2 text-[9px] bg-slate-200 text-slate-700 font-semibold">{a.type}</Badge>
                    <p className="text-xs text-slate-500">{a.description}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold">${(a.amount || 0).toLocaleString()}</span>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="border rounded-2xl bg-white overflow-hidden">
          <div className="px-5 py-3 border-b bg-slate-50">
            <h2 className="font-bold text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500" /> Recent Financial Activity</h2>
          </div>
          <div className="divide-y max-h-[280px] overflow-y-auto">
            {(data.activity || []).slice(0, 10).map((a, i) => (
              <div key={i} className="px-5 py-2 text-xs">
                <p className="text-slate-700">{a.text}</p>
                <p className="text-slate-400 mt-0.5">{a.time ? new Date(a.time).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: tz }) : ''}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs — all clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-2xl bg-white p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/orders')}>
          <DollarSign className="w-5 h-5 text-emerald-600 mb-2" />
          <p className="text-2xl font-black text-emerald-600">${(data.kpis?.month_spend || 0).toLocaleString()}</p>
          <p className="text-xs text-slate-500">Total Spend (Month)</p>
        </div>
        <div className="border rounded-2xl bg-white p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices')}>
          <Receipt className="w-5 h-5 text-red-600 mb-2" />
          <p className="text-2xl font-black text-red-600">{ab.invoices_pending || 0}</p>
          <p className="text-xs text-slate-500">Invoices Pending</p>
        </div>
        <div className="border rounded-2xl bg-white p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/orders')}>
          <ShoppingCart className="w-5 h-5 text-blue-600 mb-2" />
          <p className="text-2xl font-black text-blue-600">{data.kpis?.month_orders || 0}</p>
          <p className="text-xs text-slate-500">Orders This Month</p>
        </div>
        <div className="border rounded-2xl bg-white p-4">
          <TrendingDown className="w-5 h-5 text-amber-600 mb-2" />
          <p className="text-2xl font-black text-amber-600">{savingsData?.totals?.all_time?.saving_amount ? `$${savingsData.totals.all_time.saving_amount.toLocaleString()}` : '$0'}</p>
          <p className="text-xs text-slate-500">Savings Achieved</p>
          {savingsData?.totals?.all_time?.saving_pct > 0 && <p className="text-[10px] text-emerald-600 font-medium">{savingsData.totals.all_time.saving_pct}% vs baseline</p>}
        </div>
      </div>

      <div className="text-center pt-2">
        <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-300 hover:text-indigo-500 transition-colors">Designed by Eyvo, California</a>
      </div>
    </div>
  );
}
